import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

// Connects to data-controller="auto-detect-time-zone"
//
// Mounted on <body> only when the user has opted in via the "Use computer time
// zone" checkbox on their profile (the layout gates registration on
// current_user.auto_detect_time_zone?). On every page connect, reads
// `Intl.DateTimeFormat().resolvedOptions().timeZone`, translates the IANA name
// into our Rails-zone name via mappingValue, and PATCHes the server when it
// differs from currentValue. On a successful update we reload so the page
// renders in the freshly-saved zone (lead-distribution cutoffs, rendered
// timestamps, etc.). Server-side gate in UserSettingsController#update_time_zone
// also rechecks auto_detect_time_zone? for defense-in-depth.
//
// Falls through silently when: the browser reported an IANA we don't map (e.g.
// travel outside US/Canada), or the values already agree.
export default class extends Controller {
  static values = {
    current: String,
    mapping: Object,
    updateUrl: String
  }

  connect() {
    if (!this.updateUrlValue) return

    const iana = this.detectedIana()
    if (!iana) return

    const railsName = this.mappingValue[iana]
    if (!railsName || railsName === this.currentValue) return

    this.persist(railsName)
  }

  detectedIana() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (_e) {
      return null
    }
  }

  async persist(railsName) {
    // Network errors (offline, DNS hiccup, server 5xx) silently fall through —
    // matching the other no-op branches in connect(). The next page navigation
    // will retry detection.
    try {
      const response = await patch(this.updateUrlValue, {
        body: JSON.stringify({ time_zone: railsName }),
        contentType: "application/json"
      })
      if (response.ok) this.reload()
    } catch (_e) {
      // swallow — leave the user on the current page; auto-detect retries on next nav
    }
  }

  // Wrapped for testability — jsdom's window.location.reload is non-configurable
  // and cannot be spied on directly.
  reload() {
    window.location.reload()
  }
}
